import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3, Loader2, Calendar, TrendingUp, Package, Users,
  ShoppingCart, Receipt, Truck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const PRESETS = [
  { key: "today", label: "اليوم", from: today, to: today },
  { key: "yesterday", label: "أمس", from: () => daysAgo(1), to: () => daysAgo(1) },
  { key: "7d", label: "آخر 7 أيام", from: () => daysAgo(7), to: today },
  { key: "30d", label: "آخر 30 يوم", from: () => daysAgo(30), to: today },
];

const Stat = ({ icon: Icon, label, value, color, tid }) => (
  <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] text-center" data-testid={tid}>
    <Icon className="w-5 h-5 mx-auto mb-1" style={{ color }} />
    <p className="text-[10px] text-[#5C6E64]">{label}</p>
    <p className="text-base font-black tabular-nums" style={{ color }} dir="ltr">{value}</p>
  </div>
);

export default function SalesReportTab() {
  const [mode, setMode] = useState("sales"); // 'sales' | 'purchases'
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const endpoint = mode === "sales" ? "sales-report" : "purchases-report";
      const { data: d } = await axios.get(`${API}/admin/${endpoint}`, {
        params: { from_date: from, to_date: to },
      });
      setData(d);
    } catch {
      toast.error("فشل تحميل التقرير");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [mode, from, to]);

  const applyPreset = (p) => { setFrom(p.from()); setTo(p.to()); };

  return (
    <div className="space-y-3" dir="rtl" data-testid="sales-report-tab">
      {/* Mode toggle */}
      <div className="bg-white rounded-2xl p-2 border-2 border-[#E4EBE6] flex items-center gap-2 flex-wrap">
        <div className="flex items-center bg-[#F3F6F4] rounded-full p-0.5">
          <button
            onClick={() => setMode("sales")}
            data-testid="report-mode-sales"
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${
              mode === "sales" ? "bg-[#1E3A5F] text-white" : "text-[#5C6E64]"
            }`}
          >📈 تقرير المبيعات</button>
          <button
            onClick={() => setMode("purchases")}
            data-testid="report-mode-purchases"
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${
              mode === "purchases" ? "bg-[#1E3A5F] text-white" : "text-[#5C6E64]"
            }`}
          >🛒 تقرير المشتريات</button>
        </div>
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => applyPreset(p)}
            data-testid={`preset-${p.key}`}
            className="text-xs font-bold bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] px-3 py-1.5 rounded-full"
          >{p.label}</button>
        ))}
      </div>

      {/* Date range */}
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-3 flex items-center gap-2 flex-wrap">
        <Calendar className="w-5 h-5" />
        <span className="text-xs font-bold">من</span>
        <input
          type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          data-testid="report-from-date" max={to}
          className="bg-white/20 text-white rounded-full px-3 py-1 text-xs border border-white/30 focus:outline-none focus:bg-white/30"
        />
        <span className="text-xs font-bold">إلى</span>
        <input
          type="date" value={to} onChange={(e) => setTo(e.target.value)}
          data-testid="report-to-date" min={from} max={today()}
          className="bg-white/20 text-white rounded-full px-3 py-1 text-xs border border-white/30 focus:outline-none focus:bg-white/30"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
      </div>

      {/* Totals + body */}
      {!data ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : mode === "sales" ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat icon={TrendingUp} label="المبيعات (د.ع)" value={fmt(data.totals.revenue)} color="#15803D" tid="stat-revenue" />
            <Stat icon={Package} label="التكلفة (د.ع)" value={fmt(data.totals.cost)} color="#E85D45" tid="stat-cost" />
            <Stat icon={BarChart3} label="الربح (د.ع)" value={fmt(data.totals.profit)} color="#1E3A5F" tid="stat-profit" />
            <Stat icon={ShoppingCart} label="الكراتين" value={fmt(data.totals.quantity)} color="#3B82F6" tid="stat-qty" />
            <Stat icon={Users} label="عدد الفواتير" value={fmt(data.totals.orders_count)} color="#F59E0B" tid="stat-count" />
          </div>
          {Number(data.totals.discount) > 0 && (
            <div className="bg-[#FEF3C7] border-2 border-[#F5A623]/40 rounded-xl p-2 text-xs text-[#7c2d12] text-center">
              💰 إجمالي الخصومات المطبّقة بالفترة: <b dir="ltr">{fmt(data.totals.discount)} د.ع</b>
            </div>
          )}
          {/* Customers list */}
          <div>
            <h3 className="font-black text-sm text-[#1E3A5F] mb-2 flex items-center gap-1">
              <Users className="w-4 h-4" /> الزبائن اللي بعنا لهم ({data.customers.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {data.customers.map((c, i) => (
                <div key={c.id} data-testid={`sales-customer-${c.id}`} className="bg-white rounded-xl p-2.5 border-2 border-[#E4EBE6] hover:border-[#3B82F6] transition-colors">
                  <div className="flex justify-between items-start mb-1">
                    <p className="text-[10px] text-[#5C6E64]" dir="ltr">#{c.invoice_number || c.id.slice(0, 6)}</p>
                    {c.is_supervisor_sale && <span className="text-[9px] bg-[#FEF3C7] text-[#7c2d12] font-bold px-1.5 py-0.5 rounded-full">👤 {c.supervisor_name || "مشرف"}</span>}
                    {c.is_direct_sale && !c.is_supervisor_sale && <span className="text-[9px] bg-[#E0F2FE] text-[#1E3A5F] font-bold px-1.5 py-0.5 rounded-full">🛒 مباشر</span>}
                  </div>
                  <p className="text-sm font-bold text-[#1E3A5F] truncate">{c.user_name || c.market_name}</p>
                  <div className="flex justify-between items-end mt-1">
                    <span className="text-[10px] text-[#5C6E64]" dir="ltr">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="font-black text-[#15803D]" dir="ltr">{fmt(c.total)} د.ع</span>
                  </div>
                  {c.manual_discount > 0 && (
                    <p className="text-[10px] text-[#E85D45] font-bold mt-0.5">خصم: <span dir="ltr">−{fmt(c.manual_discount)}</span></p>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Products list */}
          <div>
            <h3 className="font-black text-sm text-[#1E3A5F] mb-2 flex items-center gap-1">
              <Package className="w-4 h-4" /> المنتجات المباعة ({data.products.length})
            </h3>
            <div className="space-y-1">
              {data.products.map((p, i) => (
                <div key={p.product_id} data-testid={`sales-product-${p.product_id}`} className="bg-white rounded-xl p-2 flex items-center gap-2 border border-[#E4EBE6]">
                  <span className="text-[10px] font-black text-[#5C6E64] w-6 text-center">#{i + 1}</span>
                  {p.image_path && <img src={`${API}/files/${p.image_path}`} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold truncate">{p.name_ar}</p>
                    <p className="text-[10px] text-[#5C6E64]" dir="ltr">× {fmt(p.quantity)} · مبيعات {fmt(p.revenue)} د.ع</p>
                  </div>
                  <span className="font-black text-[#15803D] text-xs" dir="ltr">{fmt(p.profit)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Purchases mode */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat icon={Receipt} label="إجمالي الشراء" value={fmt(data.totals.total)} color="#1E3A5F" tid="purchase-stat-total" />
            <Stat icon={TrendingUp} label="المدفوع" value={fmt(data.totals.paid)} color="#15803D" tid="purchase-stat-paid" />
            <Stat icon={Truck} label="المتبقي" value={fmt(data.totals.remaining)} color="#E85D45" tid="purchase-stat-remaining" />
            <Stat icon={Package} label="عدد الفواتير" value={fmt(data.totals.invoices_count)} color="#F59E0B" tid="purchase-stat-count" />
          </div>
          {/* Suppliers */}
          <div>
            <h3 className="font-black text-sm text-[#1E3A5F] mb-2 flex items-center gap-1">
              <Truck className="w-4 h-4" /> المُجهّزون ({data.suppliers.length})
            </h3>
            <div className="space-y-1">
              {data.suppliers.map((s, i) => (
                <div key={s.supplier_id} data-testid={`purchase-supplier-${s.supplier_id}`} className="bg-white rounded-xl p-2 flex items-center gap-2 border-2 border-[#3B82F6]/20">
                  <span className="text-[10px] font-black text-[#5C6E64] w-6 text-center">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1E3A5F] truncate">{s.supplier_name}</p>
                    <p className="text-[10px] text-[#5C6E64]" dir="ltr">{s.invoices_count} فاتورة · {fmt(s.quantity)} كرتون</p>
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] text-[#5C6E64]">إجمالي</p>
                    <p className="font-black text-[#1E3A5F]" dir="ltr">{fmt(s.total)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Invoices */}
          <div>
            <h3 className="font-black text-sm text-[#1E3A5F] mb-2 flex items-center gap-1">
              <Receipt className="w-4 h-4" /> فواتير الشراء ({data.invoices.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {data.invoices.map((inv) => (
                <div key={inv.id} data-testid={`purchase-invoice-${inv.id}`} className="bg-white rounded-xl p-2.5 border-2 border-[#E4EBE6]">
                  <div className="flex justify-between mb-1">
                    <p className="text-[10px] font-bold text-[#3B82F6]" dir="ltr">#{inv.invoice_number}</p>
                    {inv.edited_at && <span className="text-[9px] bg-[#FEF3C7] text-[#7c2d12] font-bold px-1.5 py-0.5 rounded-full">✏️ مُعدّل</span>}
                  </div>
                  <p className="text-sm font-bold text-[#1E3A5F] truncate">{inv.supplier_name}</p>
                  <div className="flex justify-between items-end mt-1">
                    <span className="text-[10px] text-[#5C6E64]" dir="ltr">{(inv.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="font-black text-[#1E3A5F]" dir="ltr">{fmt(inv.total)} د.ع</span>
                  </div>
                  <div className="flex justify-between text-[10px] text-[#5C6E64] mt-0.5">
                    <span>{inv.total_quantity} كرتون</span>
                    {inv.amount_remaining > 0 && <span className="text-[#E85D45] font-bold" dir="ltr">متبقي: {fmt(inv.amount_remaining)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
